import { HotTokensSortKeys } from 'api/types/httpsTypes/contracts';
import {
  FilterState,
  SafetyMeasuresFilterState,
  safetyMeasuresSchema,
  useHotTokensSearchFilters,
} from './usePageParams';
import { useEffect, useState } from 'react';
import { useDebouncedState } from 'utils/useDebounce';
import { FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { isNotNil } from 'modules/utils/isNotNil';
import {
  allSuffixes,
  formatNumberWithSuffix,
  safeBig,
} from 'utils/FormatNumber';
import { HotTokensPeriods, TokenLaunchPeriods } from '../Wallets/usePageParams';
import { twMerge } from 'tailwind-merge';
import { isValidAddress } from 'helpers/api/web3Utils';
import { PreSetFilters, PreSets } from '../../../bots/volatility/schema';
import {
  SortDropdown,
  SortDropdownProps,
} from '../../../components/dropdown/sort-dropdown';
import { ActivteFiltersBar, FilterTag } from '../../../components/filters';
import Button from 'modules/shared-components/button/button';
import TextInput from '../../../components/inputs/text-input/text-input';
import QuickFilters from '../../../components/filters/quickFilters';
import { useSelectedChain } from '../../../components/chains/getChains';
import {
  useForm,
  useFormContext,
  FormProvider,
  Controller,
} from 'react-hook-form';
import NumberInput from '../../../components/inputs/number-input';
import Tooltip from 'modules/shared-components/tooltip';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import CustomModal from '../../../components/modal/CustomModal';
import { PrimaryButton } from 'modules/shared-components/button/SubmitButton';
import { useToggle } from 'modules/utils/useToggle';

export const safetyMeasureConfigs = [
  {
    key: 'TokenAge',
    label: 'Token Age Range (min)',
    tooltip:
      "The bot will not trade if the token's age is less than the minimum or greater than the maximum value (in minutes).",
  },
  {
    key: 'MarketCap',
    label: 'Market Cap Range ($)',
    tooltip:
      "The bot will not trade if a token's market capitalization falls outside the specified range.",
  },
  {
    key: 'Liquidity',
    label: 'Liquidity Range ($)',
    tooltip:
      "The bot will not trade if a token's available liquidity is outside the specified range.",
  },
  {
    key: 'Volume',
    label: 'Volume Range ($)',
    tooltip:
      "The bot will not trade if a token's trading volume during the selected period is outside the specified range.",
  },
  {
    key: 'VolatilityIndex',
    label: 'Volatility Index Range',
    tooltip:
      "The bot will not trade if a token's volatility index during the selected period is outside the specified range.",
  },
  {
    key: 'SmartBuyVolume',
    label: 'Smart Buy Volume Range ($)',
    tooltip:
      'The bot will not trade if the buy volume from smart wallets during the selected period is outside the specified range.',
  },
  {
    key: 'SmartBuyWallets',
    label: 'Smart Buy Wallets Range',
    tooltip:
      'The bot will not trade if the number of smart wallets buying during the selected period is outside the specified range.',
  },
  {
    key: 'SmartSellVolume',
    label: 'Smart Sell Volume Range ($)',
    tooltip:
      'The bot will not trade if the sell volume from smart wallets during the selected period is outside the specified range.',
  },
  {
    key: 'SmartSellWallets',
    label: 'Smart Sell Wallets Range',
    tooltip:
      'The bot will not trade if the number of smart wallets selling during the selected period is outside the specified range.',
  },
  {
    key: 'SmartMoneyInflow',
    label: 'Smart Money Inflow Range ($)',
    tooltip:
      'The bot will not trade if the net money inflow from smart wallets during the selected period is outside the specified range.',
  },
];

export function FiltersForm({ isNotMaxHeight }: { isNotMaxHeight?: boolean }) {
  const form = useFormContext();
  const { control } = form;

  return (
    <div
      className={twMerge(
        isNotMaxHeight ? '' : 'lg:max-h-[500px] overflow-y-auto',
        'xxs:gap-y-3 flex flex-col xxs:max-h-screen '
      )}
    >
      {safetyMeasureConfigs.map((config) => {
        const minKey = `min${config.key}` as keyof SafetyMeasuresFilterState;
        const maxKey = `max${config.key}` as keyof SafetyMeasuresFilterState;

        return (
          <div key={config.key} className="flex flex-col gap-y-2 ">
            <div className="flex">
              <Tooltip
                className="xxs:text-dex-white-secondary hover:text-dex-white"
                text={config.tooltip}
              >
                <div className="xxs:text-sm">{config.label}</div>
              </Tooltip>
            </div>

            <div className="flex gap-x-2">
              <Controller
                name={minKey}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <NumberInput
                    {...field}
                    placeholder="Min"
                    inputSize="small"
                    error={error?.message}
                  />
                )}
              />
              <Controller
                name={maxKey}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <NumberInput
                    {...field}
                    placeholder="Max"
                    inputSize="small"
                    error={error?.message}
                  />
                )}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const PERIOD_FILTERS = [
  {
    label: '5M',
    value: HotTokensPeriods.FiveMinutes,
  },
  {
    label: '15M',
    value: HotTokensPeriods.FifteenMinutes,
  },
  {
    label: '30M',
    value: HotTokensPeriods.ThirtyMinutes,
  },
  {
    label: '1H',
    value: HotTokensPeriods.OneHour,
  },
  {
    label: '6H',
    value: HotTokensPeriods.SixHours,
  },
  {
    label: '24H',
    value: HotTokensPeriods.TwentyFourHour,
  },
];

export const TOKENS_LAUNCH_PERIOD_FILTERS = [
  {
    label: '> 1 Hour',
    value: TokenLaunchPeriods.Hour,
  },
  {
    label: '> 1 Day',
    value: TokenLaunchPeriods.Day,
  },
  {
    label: '> 1 Week',
    value: TokenLaunchPeriods.Week,
  },
  {
    label: '> 1 Month',
    value: TokenLaunchPeriods.Month,
  },
  {
    label: '> 3 Months',
    value: TokenLaunchPeriods.ThreeMonths,
  },
];

export const SORT_OPTIONS = [
  {
    label: 'Price Change',
    value: `-${HotTokensSortKeys.PriceChange}`,
  },
  {
    label: 'Volatility Index',
    value: `-${HotTokensSortKeys.VolatilityIndex}`,
  },
  {
    label: 'Smart Wallets Flow',
    value: `-${HotTokensSortKeys.SmartFlow}`,
  },
  {
    label: 'Volume',
    value: `-${HotTokensSortKeys.Volume}`,
  },
  {
    label: 'Liquidity',
    value: `-${HotTokensSortKeys.Liquidity}`,
  },
  {
    label: 'Market Cap',
    value: `-${HotTokensSortKeys.MarketCap}`,
  },
];

interface Props {
  handleClose: () => void;
}

const FiltersModal = ({ handleClose }: Props) => {
  const { filtersState, setFilters } = useHotTokensSearchFilters();

  const selectedChain = useSelectedChain();
  const [hotTokenAddress, debouncedHotTokenAddress, setHotTokenAddress] =
    useDebouncedState('', 250);
  const [addressError, setAddressError] = useState(false);
  const updateFilters = (update: Partial<FilterState>) => {
    setFilters({ ...filtersState, ...update });
  };

  useEffect(() => {
    if (debouncedHotTokenAddress) {
      setAddressError(!isValidAddress(debouncedHotTokenAddress, selectedChain));
      if (isValidAddress(debouncedHotTokenAddress, selectedChain)) {
        updateFilters({
          hotTokenAddress: debouncedHotTokenAddress,
        });
      }
    }
  }, [debouncedHotTokenAddress]);

  const handleWalletAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setHotTokenAddress(inputValue);
    if (!inputValue) {
      setAddressError(false);
      updateFilters({
        hotTokenAddress: undefined,
      });
    }
  };

  const methods = useForm<SafetyMeasuresFilterState>({
    resolver: yupResolver(yup.object(safetyMeasuresSchema)),
    defaultValues: filtersState,
  });

  const { handleSubmit, reset } = methods;

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    void handleSubmit((form) => {
      const updatedFilters = { ...filtersState };

      safetyMeasureConfigs.forEach(({ key }) => {
        const minKey = `min${key}` as keyof typeof form;
        const maxKey = `max${key}` as keyof typeof form;

        updatedFilters[minKey] = form[minKey] || undefined;
        updatedFilters[maxKey] = form[maxKey] || undefined;
      });

      setFilters(updatedFilters);
      handleClose();
    })(e);
  };

  const onReset = () => {
    const clearedFilters: Partial<SafetyMeasuresFilterState> = {};

    safetyMeasureConfigs.forEach(({ key }) => {
      const minKey = `min${key}` as keyof SafetyMeasuresFilterState;
      const maxKey = `max${key}` as keyof SafetyMeasuresFilterState;

      clearedFilters[minKey] = undefined;
      clearedFilters[maxKey] = undefined;
    });

    reset(clearedFilters);
    setFilters({ ...filtersState, ...clearedFilters });
    handleClose();
  };

  return (
    <CustomModal
      showModal
      size="xLarge"
      title="Advanced Filters"
      handleClose={handleClose}
    >
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} autoComplete="off">
          <CustomModal.Body>
            <CustomModal.Title className="hidden lg:flex">
              Use advanced filters to find the best tokens.
            </CustomModal.Title>

            <CustomModal.Content>
              <div className="xxs:gap-y-2 flex flex-col">
                <div>
                  <TextInput
                    name="hotTokenAddress"
                    error={addressError ? 'Address not valid' : ''}
                    value={hotTokenAddress}
                    prefix={
                      <MagnifyingGlassIcon className="w-4 xxs:text-dex-white-secondary" />
                    }
                    onChange={handleWalletAddressInputChange}
                    placeholder="Search Address"
                  />
                </div>
                <FiltersForm />
              </div>
            </CustomModal.Content>
          </CustomModal.Body>
          <CustomModal.Footer>
            <PrimaryButton type="submit" className="xxs:text-base lg:text-xs">
              Save
            </PrimaryButton>
            <Button
              className="xxs:text-base lg:text-xs"
              type="button"
              variant="dexNeutral"
              onClick={onReset}
            >
              Reset
            </Button>
          </CustomModal.Footer>
        </form>
      </FormProvider>
    </CustomModal>
  );
};

export const HotTokensFiltersBar = () => {
  const [openModalFilters, { open, close }] = useToggle();
  const { filtersState: filters, setFilters } = useHotTokensSearchFilters();
  const selectedChain = useSelectedChain();
  const [hotTokenAddress, debouncedHotTokenAddress, setHotTokenAddress] =
    useDebouncedState('', 250);
  const [addressError, setAddressError] = useState(false);
  useEffect(() => {
    if (debouncedHotTokenAddress) {
      setAddressError(!isValidAddress(debouncedHotTokenAddress, selectedChain));
      if (isValidAddress(debouncedHotTokenAddress, selectedChain)) {
        updateFilters({
          hotTokenAddress: debouncedHotTokenAddress,
        });
      }
    }
  }, [debouncedHotTokenAddress]);

  const handleWalletAddressInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.preventDefault();

    const inputValue = e.target.value;
    setHotTokenAddress(inputValue);
    if (!inputValue) {
      setAddressError(false);
      updateFilters({
        hotTokenAddress: undefined,
      });
    }
  };

  const updateFilters = (update: Partial<FilterState>) => {
    setFilters({ ...filters, ...update });
  };

  const quickFilterMostVolatility = () => {
    return setFilters({
      ...PreSetFilters[PreSets.Volatility],
    } as FilterState);
  };

  const quickFilterMostTraded = () => {
    return setFilters({
      ...PreSetFilters[PreSets.TradingVolume],
    } as FilterState);
  };

  const quickFilterSmartVolume = () => {
    return setFilters({
      ...PreSetFilters[PreSets.SmartMoney],
    } as FilterState);
  };

  const quickFilterMostPriceChange = () => {
    return setFilters({
      ...PreSetFilters[PreSets.Price],
    } as FilterState);
  };

  const quickFilterOptions = [
    {
      label: 'Most Volatile',
      onClick: quickFilterMostVolatility,
      condition: filters.sort === '-volatility_index',
      extraClasses: 'flex',
    },
    {
      label: 'Most Traded',
      onClick: quickFilterMostTraded,
      condition: filters.sort === '-volume',
      extraClasses: 'flex',
    },
    {
      label: 'Smart Wallets Volume',
      onClick: quickFilterSmartVolume,
      condition: filters.sort === '-buy_volume_top',
      extraClasses: 'xxs:hidden lg:flex',
    },
    {
      label: 'Price Increase',
      onClick: quickFilterMostPriceChange,
      condition: filters.sort === '-price_change',
      extraClasses: 'flex',
    },
  ];

  const nOfFiltersActive = [
    filters.hotTokenAddress,
    filters.minTokenAge,
    filters.maxTokenAge,
    filters.minVolume,
    filters.maxVolume,
    filters.minLiquidity,
    filters.maxLiquidity,
    filters.minMarketCap,
    filters.maxMarketCap,
    filters.minSmartBuyVolume,
    filters.maxSmartBuyVolume,
    filters.minVolatilityIndex,
    filters.maxVolatilityIndex,
    filters.minSmartBuyWallets,
    filters.maxSmartBuyWallets,
    filters.minSmartMoneyInflow,
    filters.maxSmartMoneyInflow,
    filters.minSmartSellVolume,
    filters.maxSmartSellVolume,
    filters.minSmartSellWallets,
    filters.maxSmartSellWallets,
  ].filter((value) => value !== undefined && value && value !== '').length;

  return (
    <div className="xxs:space-y-2 lg:space-y-1">
      <div className="lg:hidden">
        <PeriodFilterButtons
          value={filters.period}
          onChange={(value) => {
            updateFilters({
              period: value as HotTokensPeriods,
            });
          }}
        />
      </div>
      <div className="flex justify-between">
        <div className="hidden lg:block">
          <PeriodFilterButtons
            value={filters.period}
            onChange={(value) => {
              updateFilters({
                period: value as HotTokensPeriods,
              });
            }}
          />
        </div>

        <div className="flex xxs:gap-x-2 items-center">
          <div className="xxs:w-1/4 lg:w-auto">
            <TextInput
              name="hotTokenAddress"
              error={addressError ? 'Address not valid' : ''}
              value={hotTokenAddress}
              prefix={
                <MagnifyingGlassIcon className="w-4 xxs:text-dex-white-secondary" />
              }
              onChange={handleWalletAddressInputChange}
              placeholder="Search Address"
            />
          </div>

          <div className="w-full lg:hidden">
            <Sort
              value={filters.sort}
              onChange={(value) => {
                updateFilters({
                  sort: value,
                });
              }}
            />
          </div>

          <Button
            variant="dexNeutral"
            className="m-0 xxs:p-1 min-w-[50px] lg:w-auto xxs:h-full lg:h-auto"
            onClick={open}
            type="button"
          >
            <div
              className={twMerge(
                'xxs:p-1 flex xxs:space-x-1 items-center',
                nOfFiltersActive
                  ? 'xxs:hover:text-dex-white'
                  : ' xxs:hover:text-dex-white xxs:text-dex-white-secondary xxs:px-2 '
              )}
            >
              <FunnelIcon aria-hidden="true" width={16} height={16} />
              <div className="normal-case hidden lg:block">Filters</div>
              {nOfFiltersActive ? <div>({nOfFiltersActive})</div> : ''}
            </div>
          </Button>
        </div>

        {openModalFilters && <FiltersModal handleClose={close} />}
      </div>

      <QuickFilters filterOptions={quickFilterOptions} />
      <ActivteFiltersBar>
        {filters.hotTokenAddress && (
          <FilterTag
            onRemove={() => {
              setHotTokenAddress('');
              return updateFilters({
                hotTokenAddress: undefined,
              });
            }}
            label={`Token: ${
              filters?.hotTokenAddress ?? hotTokenAddress ?? '?'
            }`}
          />
        )}

        {safetyMeasureConfigs.map(({ key, label }) => {
          const minKey = `min${key}` as keyof typeof filters;
          const maxKey = `max${key}` as keyof typeof filters;

          return (
            <>
              {isNotNil(filters[minKey]) && (
                <FilterTag
                  key={`${key}-min`}
                  onRemove={() => updateFilters({ [minKey]: undefined })}
                  label={`${
                    label.split(' Range')[0]
                  } > ${formatNumberWithSuffix(safeBig(filters[minKey]), {
                    suffixes: allSuffixes,
                    precision: 0,
                  })}`}
                />
              )}

              {isNotNil(filters[maxKey]) && (
                <FilterTag
                  key={`${key}-max`}
                  onRemove={() => updateFilters({ [maxKey]: undefined })}
                  label={`${
                    label.split(' Range')[0]
                  } < ${formatNumberWithSuffix(safeBig(filters[maxKey]), {
                    suffixes: allSuffixes,
                    precision: 0,
                  })}`}
                />
              )}
            </>
          );
        })}
      </ActivteFiltersBar>
    </div>
  );
};

export function Sort(props: Omit<SortDropdownProps<string>, 'options'>) {
  return (
    <SortDropdown
      size="xs"
      className="xxs:p-2 h-auto"
      {...props}
      options={SORT_OPTIONS}
    />
  );
}

export function PeriodFilterButtons(
  props: Omit<SortDropdownProps<string>, 'options'>
) {
  return (
    <div className="xxs:rounded xxs:grid xxs:grid-cols-6 xxs:divide-x xxs:divide-dex-black-700 border-tailwind xxs:border-solid xxs:border-dex-black-700 ">
      {PERIOD_FILTERS.map((period, id) => {
        return (
          <Button
            size="small"
            variant="dexNeutral"
            className={twMerge(
              'xxs:m-0 xxs:rounded-none xxs:px-2 xxs:hover:bg-dex-black-700',
              id === 0 && 'xxs:rounded-l-sm',
              id === PERIOD_FILTERS.length - 1 && 'xxs:rounded-r-sm',
              period.value === (props.value as HotTokensPeriods)
                ? 'xxs:bg-dex-black-700 xxs:text-dex-white'
                : 'xxs:bg-dex-black-800 xxs:text-dex-white-secondary hover:text-dex-white'
            )}
            key={id}
            type="button"
            onClick={() => props.onChange(period.value)}
          >
            <div className="xxs:text-xs">{period.label}</div>
          </Button>
        );
      })}
    </div>
  );
}

export function PeriodFilter(
  props: Omit<SortDropdownProps<string>, 'options'>
) {
  return (
    <SortDropdown
      size="xs"
      className="xxs:py-2 lg:py-1.5 xxs:px-3 lg:px-2 h-auto"
      {...props}
      options={PERIOD_FILTERS}
      mainLabel="Period"
    />
  );
}

export function TokenLaunchPeriodFilterMobile(
  props: Omit<SortDropdownProps<TokenLaunchPeriods>, 'options'>,
  size?: string
) {
  return (
    <SortDropdown
      size={size}
      className="xxs:py-2 xxs:px-3 h-auto"
      {...props}
      options={TOKENS_LAUNCH_PERIOD_FILTERS}
      mainLabel="Token Age"
    />
  );
}
